import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, switchMap, of } from 'rxjs';
import type {
  AuthMeDto,
  AuthTokensDto,
  CreateFamilyRequest,
  CreateStudentRequest,
  FamilyDto,
  FamilyMemberDto,
  GrandparentDashboardDto,
  InvitationDto,
  ParentDashboardDto,
  StudentDashboardDto,
  StudentDto,
  UserDto,
} from '@tattva/types';
import { environment } from '../../../environments/environment';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly base = environment.apiBaseUrl;

  register(body: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }) {
    return this.http.post<{ message: string; userId: string; email: string }>(
      `${this.base}/auth/register`,
      body,
    );
  }

  verifyEmail(email: string, code: string) {
    return this.http.post<{ message: string }>(`${this.base}/auth/verify-email`, { email, code });
  }

  login(email: string, password: string, rememberMe = false) {
    return this.http
      .post<{ tokens: AuthTokensDto; user: UserDto }>(`${this.base}/auth/login`, {
        email,
        password,
        rememberMe,
      })
      .pipe(
        tap((res) => this.authStore.setTokens(res.tokens)),
        switchMap(() => this.loadMe()),
      );
  }

  refresh(refreshToken: string) {
    return this.http.post<AuthTokensDto>(`${this.base}/auth/refresh`, { refreshToken }).pipe(
      tap((tokens) => this.authStore.setTokens(tokens)),
    );
  }

  logout() {
    const refreshToken = this.authStore.getRefreshToken();
    return this.http.post(`${this.base}/auth/logout`, { refreshToken }).pipe(
      tap(() => this.authStore.clear()),
    );
  }

  loadMe(): Observable<AuthMeDto> {
    return this.http.get<AuthMeDto>(`${this.base}/auth/me`).pipe(tap((me) => this.authStore.setMe(me)));
  }

  forgotPassword(email: string) {
    return this.http.post<{ message: string }>(`${this.base}/auth/password/forgot`, { email });
  }

  resetPassword(email: string, code: string, newPassword: string) {
    return this.http.post<{ message: string }>(`${this.base}/auth/password/reset`, {
      email,
      code,
      newPassword,
    });
  }

  googleLogin(idToken: string) {
    return this.http
      .post<{ tokens: AuthTokensDto; user: UserDto }>(`${this.base}/auth/google`, { idToken })
      .pipe(
        tap((res) => this.authStore.setTokens(res.tokens)),
        switchMap(() => this.loadMe()),
      );
  }

  createFamily(body: CreateFamilyRequest) {
    return this.http.post<FamilyDto>(`${this.base}/families`, body);
  }

  getFamily(familyId: string) {
    return this.http.get<FamilyDto>(`${this.base}/families/${familyId}`);
  }

  listMembers(familyId: string) {
    return this.http.get<FamilyMemberDto[]>(`${this.base}/families/${familyId}/members`);
  }

  inviteMember(
    familyId: string,
    body: { email?: string; phone?: string; role: string; relationship?: string },
  ) {
    return this.http.post<InvitationDto>(`${this.base}/families/${familyId}/invitations`, body);
  }

  listInvitations(familyId: string) {
    return this.http.get<InvitationDto[]>(`${this.base}/families/${familyId}/invitations`);
  }

  acceptInvitation(token: string, body?: {
    password?: string;
    firstName?: string;
    lastName?: string;
  }) {
    return this.http.post<{ familyId: string; message: string }>(
      `${this.base}/families/invitations/accept`,
      { token, ...body },
    );
  }

  updateOnboarding(familyId: string, step: string) {
    return this.http.patch<FamilyDto>(`${this.base}/families/${familyId}/onboarding`, { step });
  }

  listBoards() {
    return this.http.get<Array<{ id: string; code: string; name: string }>>(`${this.base}/boards`);
  }

  listSubjects() {
    return this.http.get<Array<{ id: string; code: string; name: string }>>(`${this.base}/subjects`);
  }

  listAcademicYears() {
    return this.http.get<Array<{ id: string; label: string; isCurrent: boolean }>>(
      `${this.base}/academic-years`,
    );
  }

  listSchools(boardId?: string) {
    const q = boardId ? `?boardId=${boardId}` : '';
    return this.http.get<Array<{ id: string; name: string; boardId: string }>>(
      `${this.base}/schools${q}`,
    );
  }

  createSchool(body: { name: string; boardId: string; city?: string; state?: string }) {
    return this.http.post<{ id: string; name: string }>(`${this.base}/schools`, body);
  }

  createStudent(familyId: string, body: CreateStudentRequest) {
    return this.http.post<StudentDto>(`${this.base}/families/${familyId}/students`, body);
  }

  listStudents(familyId: string) {
    return this.http.get<StudentDto[]>(`${this.base}/families/${familyId}/students`);
  }

  parentDashboard(familyId: string) {
    return this.http.get<ParentDashboardDto>(`${this.base}/dashboard/parent/${familyId}`);
  }

  studentDashboard(studentId: string) {
    return this.http.get<StudentDashboardDto>(`${this.base}/dashboard/student/${studentId}`);
  }

  grandparentDashboard(familyId: string, studentId: string) {
    return this.http.get<GrandparentDashboardDto>(
      `${this.base}/dashboard/grandparent/${familyId}?studentId=${studentId}`,
    );
  }

  health() {
    return this.http.get(`${this.base}/health`);
  }

  ensureSession(): Observable<AuthMeDto | null> {
    if (!this.authStore.accessToken()) {
      return of(null);
    }
    return this.loadMe();
  }
}
